// @oasis-permission-inherits: /ai-edits/evals (evals:view)
import { Link, Navigate, createFileRoute } from "@tanstack/react-router";

import { Badge } from "~/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";
import { formatAgaveEvalDate } from "~/modules/agave-evals/formatters";
import { isHarborPairwise } from "~/modules/agave-evals/harbor-pairwise";
import {
  isPairwiseComparison,
  parsePairwisePairs,
  tallyPairwise,
  tallyPairwiseByCase,
  type PairwisePair,
  type PairwiseTally,
  type PairwiseWinner,
} from "~/modules/agave-evals/pairwise";
import { getAgaveEvalComparisonQuery } from "~/modules/agave-evals/queries";
import { usePageTitle } from "~/modules/page-title/page-title";

export const Route = createFileRoute("/_dash/ai-edits/evals/pairwise/$comparisonId")({
  component: RouteComponent,
});

const winnerLabels: Record<PairwiseWinner, string> = {
  a: "Baseline",
  b: "Candidate",
  tie: "Tie",
  unjudged: "Unjudged",
};

function WinnerBadge({ winner }: { winner: PairwiseWinner }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        winner === "b" && "border-emerald-500/50 text-emerald-600",
        winner === "a" && "border-amber-500/50 text-amber-600",
        winner === "unjudged" && "border-destructive/50 text-destructive"
      )}
    >
      {winnerLabels[winner]}
    </Badge>
  );
}

function TallyLine({ tally }: { tally: PairwiseTally }) {
  return (
    <span className="text-sm text-muted-foreground">
      Candidate {tally.b} · Baseline {tally.a} · Ties {tally.tie}
      {tally.unjudged > 0 ? ` · Unjudged ${tally.unjudged}` : ""}
    </span>
  );
}

function PairRow({ pair }: { pair: PairwisePair }) {
  const details = pair.repetitions.filter((repetition) => repetition.reasoning || repetition.error);
  return (
    <TableRow>
      <TableCell className="font-mono text-xs">{pair.caseName}</TableCell>
      <TableCell className="whitespace-nowrap text-xs">
        b{pair.baselineTrial ?? "?"} vs c{pair.candidateTrial ?? "?"}
      </TableCell>
      <TableCell>
        <WinnerBadge winner={pair.winner} />
      </TableCell>
      <TableCell className="font-mono text-xs">{pair.picks.join(" ") || "—"}</TableCell>
      <TableCell className="max-w-xl">
        {details.length === 0 ? (
          <span className="text-xs text-muted-foreground">No judge output recorded.</span>
        ) : (
          <Collapsible>
            <CollapsibleTrigger className="text-xs font-medium text-primary underline-offset-4 hover:underline">
              Judge output ({details.length})
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="mt-2 space-y-2">
                {details.map((repetition) => (
                  <li key={repetition.repetition} className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      Rep {repetition.repetition}
                      {repetition.winner ? ` (${winnerLabels[repetition.winner]})` : ""}:
                    </span>{" "}
                    {repetition.reasoning ?? (
                      <span className="text-destructive">Judge failed: {repetition.error}</span>
                    )}
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}
      </TableCell>
    </TableRow>
  );
}

function RouteComponent() {
  const { comparisonId } = Route.useParams();
  usePageTitle(`Pairwise ${comparisonId}`);
  const {
    data: comparison,
    isLoading,
    error,
  } = getAgaveEvalComparisonQuery.useQuery({
    pathParams: { comparisonId },
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !comparison) {
    return <div className="text-sm text-destructive">Could not load this comparison.</div>;
  }

  if (isHarborPairwise(comparison)) {
    return (
      <Navigate
        to="/ai-edits/evals/comparisons/$comparisonId"
        params={{ comparisonId }}
        search={{ kind: comparison.evalSuite === "chat" ? "chat" : "creation", legacy: false }}
        replace
      />
    );
  }

  if (!isPairwiseComparison(comparison)) {
    return (
      <div className="text-sm text-muted-foreground">
        This comparison is not a pairwise comparison. Open it on the{" "}
        <Link
          className="font-medium text-primary underline-offset-4 hover:underline"
          to="/ai-edits/evals/comparisons/$comparisonId"
          params={{ comparisonId }}
        >
          standard comparison page
        </Link>
        .
      </div>
    );
  }

  const pairs = parsePairwisePairs(comparison);
  const overall = tallyPairwise(pairs);
  const byCase = tallyPairwiseByCase(pairs);

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold">FX pairwise comparison</h1>
        <p className="text-sm text-muted-foreground">
          Published {formatAgaveEvalDate(comparison.createdAt)} · one question per pair: which video
          is better.
        </p>
        <div className="flex flex-wrap gap-4 pt-1 text-sm">
          <Link
            className="font-medium text-primary underline-offset-4 hover:underline"
            to="/ai-edits/evals/$runId"
            params={{ runId: comparison.baselineRunId }}
          >
            Baseline run
          </Link>
          <Link
            className="font-medium text-primary underline-offset-4 hover:underline"
            to="/ai-edits/evals/$runId"
            params={{ runId: comparison.candidateRunId }}
          >
            Candidate run
          </Link>
        </div>
      </div>

      <div className="rounded-md border p-4">
        <div className="text-sm font-semibold">Overall</div>
        <TallyLine tally={overall} />
        <div className="mt-3 space-y-1">
          {[...byCase.entries()].map(([caseName, tally]) => (
            <div key={caseName} className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-xs">{caseName}</span>
              <TallyLine tally={tally} />
            </div>
          ))}
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Case</TableHead>
            <TableHead>Pair</TableHead>
            <TableHead>Winner</TableHead>
            <TableHead>Picks</TableHead>
            <TableHead>Reasoning</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pairs.map((pair) => (
            <PairRow
              key={`${pair.caseName}-${pair.baselineTrial}-${pair.candidateTrial}`}
              pair={pair}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
